import React from "react";
import { useState } from "react";
import { 
  Folder, ChevronDown, Clock, Plus, MoreHorizontal, 
  Copy, Pencil, Trash2, Check, X, Loader2 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

interface Project {
  id: string;
  name: string;
  updated_at?: string;
  status?: 'building' | 'ready';
}

interface ProjectSwitcherProps {
  currentProject: Project;
  projects: Project[];
  onSelectProject: (project: Project) => void;
  onNewProject: () => void;
  onRenameProject: (projectId: string, newName: string) => Promise<boolean>;
  onDeleteProject: (projectId: string) => Promise<boolean>;
  onDuplicateProject: (projectId: string) => Promise<any>;
}

function formatTime(dateStr?: string): string {
  if (!dateStr) return "";
  const now = new Date();
  const date = new Date(dateStr);
  const mins = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function ProjectSwitcher({
  currentProject,
  projects,
  onSelectProject,
  onNewProject,
  onRenameProject,
  onDeleteProject,
  onDuplicateProject,
}: ProjectSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState<string | null>(null);

  const handleStartRename = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(project.id);
    setEditName(project.name);
  };

  const handleSaveRename = async (projectId: string) => {
    if (editName.trim()) {
      await onRenameProject(projectId, editName.trim());
    }
    setEditingId(null);
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleDeleteClick = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;
    setIsDeleting(true);
    await onDeleteProject(projectToDelete.id);
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleDuplicate = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDuplicating(project.id);
    await onDuplicateProject(project.id);
    setIsDuplicating(null);
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-muted transition-colors">
            <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
              <Folder className="h-3.5 w-3.5 text-primary" />
            </div>
            <div className="flex flex-col items-start">
              <span className="font-medium text-sm truncate max-w-[140px]">
                {currentProject.name}
              </span>
              {currentProject.status && (
                <span className={cn(
                  "text-[10px] uppercase tracking-wide",
                  currentProject.status === 'building' ? "text-amber-500" : "text-green-500"
                )}>
                  {currentProject.status}
                </span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-80">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-medium text-muted-foreground">
              My Apps
            </p>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {projects.map((project) => (
              <div
                key={project.id}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded-md mx-1 my-0.5 cursor-pointer group",
                  project.id === currentProject.id && "bg-muted"
                )}
                onClick={() => {
                  if (editingId !== project.id) {
                    onSelectProject(project);
                    setOpen(false);
                  }
                }}
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Folder className="h-4 w-4 text-primary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  {editingId === project.id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveRename(project.id);
                          if (e.key === "Escape") handleCancelRename();
                        }}
                        className="h-7 text-sm"
                        autoFocus
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveRename(project.id);
                        }}
                      >
                        <Check className="h-3.5 w-3.5 text-green-500" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelRename();
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium truncate">{project.name}</p>
                      <div className="flex items-center gap-2">
                        {project.updated_at && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(project.updated_at)}
                          </span>
                        )}
                        {project.status && (
                          <span className={cn(
                            "text-[10px] px-1.5 py-0.5 rounded-full uppercase font-medium",
                            project.status === 'building' 
                              ? "bg-amber-500/10 text-amber-500" 
                              : "bg-green-500/10 text-green-500"
                          )}>
                            {project.status}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {editingId !== project.id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem onClick={(e) => handleStartRename(project, e as any)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => handleDuplicate(project, e as any)}
                        disabled={isDuplicating === project.id}
                      >
                        {isDuplicating === project.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Copy className="h-4 w-4 mr-2" />
                        )}
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => handleDeleteClick(project, e as any)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onNewProject} className="mx-1 my-0.5">
            <Plus className="h-4 w-4 mr-2" />
            Create New App
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{projectToDelete?.name}"? This action cannot be undone and all project files will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
